import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../assets/images/navbar-logo.png";
import {
  MdOutlineKeyboardArrowDown,
  MdKeyboardArrowRight,
} from "react-icons/md";
import "../Navbar/Navbar.css";
import LanguageSelector from "../../LanguageSelector";

export default function Navbar({ onLanguageChange, currentLanguage = "en" }) {
  const [openServices, setOpenServices] = useState(false);
  const [openIndustries, setOpenIndustries] = useState(false);
  const [hoverParentServices, setHoverParentServices] = useState(null);
  const [hoverParentIndustries, setHoverParentIndustries] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // Check if French language is active
  const isFrench = currentLanguage === "fr";

  // ------------------ TRANSLATIONS ------------------
  const translations = {
    en: {
      home: "Home",
      aboutUs: "About Us",
      services: "Services",
      industries: "Industries We Serve",
      insight: "Insight",
      career: "Career",
      contactUs: "Contact Us",
      servicesMenu: [
        {
          title: "Audit and Assurance",
          children: [
            "Financial Statement Audit",
            "IFRS Advisory Service",
            "Other Assurance Service",
            "Internal Control Framework",
          ],
        },
        {
          title: "Advisory",
          children: [
            "Deal Advisory",
            "Restructuring",
            "Due Diligence",
            "Forensic Services",
            "Risk Advisory",
            "Technology Advisory Service",
            "Valuations",
          ],
        },
        {
          title: "Taxation",
          children: [
            "Corporate Tax",
            "Merger and Acquisition",
            "Transfer Pricing",
            "Value Added Tax",
            "International Tax",
            "Excise Tax",
            "Tax Restructuring",
            "Economic Substance Regulations",
          ],
        },
        {
          title: "Business Service Outsourcing",
          children: [
            "Finance and Accounting",
            "Corporate Advisory Services",
            "Expenses Claim Management",
            "Payroll & HR Services",
            "Secondment",
            "Treasury Service",
          ],
        },
        {
          title: "Strategies",
          children: ["Market Research", "Business Consulting"],
        },
        {
          title: "Corporate Services",
          children: [
            "Entity Set-up",
            "Outsourced Finance & Regulatory Compliance",
            "Company Secertarial",
          ],
        },
      ],
      industriesMenu: [
        {
          title: "Natural Resources and Energy",
          children: [
            "Oil and Gas",
            "Power and Utilities",
            "Waste Management",
            "Mining and Minerals",
          ],
        },
        {
          title: "Financial Services",
          children: [
            "Asset Management",
            "Banking",
            "Financial Advisory Services",
            "Financial Service Tax",
            "Fintech",
            "Insurance",
          ],
        },
        { title: "Health Care", children: [] },
        { title: "Life Science", children: [] },
        { title: "Private Equity", children: [] },
        { title: "Professional Service", children: [] },
        { title: "Public Sector", children: [] },
        { title: "Real Estate & Construction", children: [] },
        {
          title: "Technology, Media, Entertainment & Telecommunications",
          children: ["Media and Entertainment", "Technology", "Telecommunication"],
        },
      ],
    },
    fr: {
      home: "Accueil",
      aboutUs: "À Propos",
      services: "Services",
      industries: "Secteurs Que Nous Servons",
      insight: "Perspectives",
      career: "Carrière",
      contactUs: "Contactez-Nous",
      servicesMenu: [
        {
          title: "Audit et Assurance",
          children: [
            "Audit des États Financiers",
            "Service Conseil IFRS",
            "Autre Service d'Assurance",
            "Cadre de Contrôle Interne",
          ],
        },
        {
          title: "Conseil",
          children: [
            "Conseil en Transaction",
            "Restructuration",
            "Due Diligence",
            "Services Forensiques",
            "Conseil en Risque",
            "Service Conseil en Technologie",
            "Évaluations",
          ],
        },
        {
          title: "Fiscalité",
          children: [
            "Impôt sur les Sociétés",
            "Fusion et Acquisition",
            "Prix de Transfert",
            "Taxe sur la Valeur Ajoutée",
            "Fiscalité Internationale",
            "Taxe d'Accise",
            "Restructuration Fiscale",
            "Réglementations sur la Substance Économique",
          ],
        },
        {
          title: "Externalisation de Services aux Entreprises",
          children: [
            "Finance et Comptabilité",
            "Services Conseil aux Entreprises",
            "Gestion des Demandes de Remboursement",
            "Services Paie & RH",
            "Détachement",
            "Service de Trésorerie",
          ],
        },
        {
          title: "Stratégies",
          children: ["Étude de Marché", "Conseil en Affaires"],
        },
        {
          title: "Services aux Entreprises",
          children: [
            "Création d'Entité",
            "Finance Externalisée & Conformité Réglementaire",
            "Secrétariat d'Entreprise",
          ],
        },
      ],
      industriesMenu: [
        {
          title: "Ressources Naturelles et Énergie",
          children: [
            "Pétrole et Gaz",
            "Électricité et Services Publics",
            "Gestion des Déchets",
            "Mines et Minéraux",
          ],
        },
        {
          title: "Services Financiers",
          children: [
            "Gestion d'Actifs",
            "Banque",
            "Services Conseil Financiers",
            "Fiscalité des Services Financiers",
            "Fintech",
            "Assurance",
          ],
        },
        { title: "Santé", children: [] },
        { title: "Sciences de la Vie", children: [] },
        { title: "Capital-Investissement", children: [] },
        { title: "Service Professionnel", children: [] },
        { title: "Secteur Public", children: [] },
        { title: "Immobilier et Construction", children: [] },
        {
          title: "Technologie, Médias, Divertissement et Télécommunications",
          children: ["Médias et Divertissement", "Technologie", "Télécommunication"],
        },
      ],
    },
  };

  const t = translations[currentLanguage] || translations.en;

  // Get the appropriate menu data
  const servicesMenu = t.servicesMenu;
  const industriesMenu = t.industriesMenu;

  // ------------------ HELPERS ------------------
  const clean = (txt) => txt.toLowerCase().replace(/[^a-z0-9]+/g, "");

  // Always use English titles for URL generation
  const getEnglishTitle = (title, menuType) => {
    const englishMenus = translations.en[menuType];
    const currentMenus = t[menuType];
    
    const index = currentMenus.findIndex(item => item.title === title);
    if (index !== -1 && englishMenus[index]) {
      return englishMenus[index].title;
    }
    return title;
  };

  const getEnglishChildTitle = (parentTitle, childTitle, menuType) => {
    const englishMenus = translations.en[menuType];
    const currentMenus = t[menuType];
    
    const parentIndex = currentMenus.findIndex(item => item.title === parentTitle);
    if (parentIndex !== -1 && englishMenus[parentIndex]) {
      const childIndex = currentMenus[parentIndex].children.findIndex(child => child === childTitle);
      if (childIndex !== -1 && englishMenus[parentIndex].children[childIndex]) {
        return englishMenus[parentIndex].children[childIndex];
      }
    }
    return childTitle;
  };

  const makePath = (base, parent, child = null, menuType) => {
    const englishParent = getEnglishTitle(parent, menuType);
    if (child) {
      const englishChild = getEnglishChildTitle(parent, child, menuType);
      return `/${base}/${clean(englishParent)}/${clean(englishChild)}`;
    }
    return `/${base}/${clean(englishParent)}`;
  };

  const handleChildClick = (path) => {
    navigate(path);
    setMenuOpen(false);
    setOpenServices(false);
    setOpenIndustries(false);
    setHoverParentServices(null);
    setHoverParentIndustries(null);
  };

  const isMobileViewport = () =>
    typeof window !== "undefined" && window.innerWidth <= 991;

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 991) {
        setMenuOpen(false);
        setOpenServices(false);
        setOpenIndustries(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const changeLanguage = (lang) => {
    if (onLanguageChange) {
      onLanguageChange(lang);
    }
  };

  return (
    <nav className="navbar navbar-expand-lg custom-navbar">
      <div className="container-fluid navbar-container">
        <a className="navbar-brand" href="/">
          <img src={Logo} alt="logo" className="navbar-logo" />
        </a>

        <button
          className={`navbar-toggler ${menuOpen ? "collapsed-open" : ""}`}
          type="button"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((s) => !s)}
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className={`collapse navbar-collapse ${menuOpen ? "show" : ""}`}>
          <ul className="navbar-nav mx-auto nav-links">
            <li className="nav-item">
              <a
                href="/"
                className={`nav-link nav-btn ${
                  currentPath === "/" ? "active-link" : ""
                }`}
              >
                {t.home}
              </a>
            </li>

            <li className="nav-item">
              <a
                href="/about"
                className={`nav-link nav-btn ${
                  currentPath === "/about" ? "active-link" : ""
                }`}
              >
                {t.aboutUs}
              </a>
            </li>

            {/* ---------------- SERVICES ---------------- */}
            <li
              className="nav-item dropdown dropdown-parent"
              onMouseEnter={() => !isMobileViewport() && setOpenServices(true)}
              onMouseLeave={() =>
                !isMobileViewport() &&
                (setOpenServices(false), setHoverParentServices(null))
              }
            >
              <div
                className="nav-link nav-btn dropdown-toggle-custom"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMobileViewport()) setOpenServices((s) => !s);
                }}
              >
                <span>{t.services}</span>
                <MdOutlineKeyboardArrowDown
                  size={20}
                  className="arrow-icon-nav"
                />
              </div>

              {openServices && <div className="dropdown-bridge"></div>}

            {(openServices || (!isMobileViewport() && openServices)) && (
  <div className="dropdown-box">
    {servicesMenu.map((item, index) => (
      <div
        key={index}
        className={`dropdown-item-row ${
          hoverParentServices === index ? "active-parent" : ""
        }`}
        onMouseEnter={() =>
          !isMobileViewport() && setHoverParentServices(index)
        }
        onClick={(e) => {
          if (isMobileViewport()) {
            if (item.children.length === 0) {
              // Navigate only if no children
              handleChildClick(makePath("services", item.title, null, "servicesMenu"));
            } else {
              // Toggle submenu if has children
              e.stopPropagation();
              setHoverParentServices((prev) =>
                prev === index ? null : index
              );
            }
          }
        }}
      >
        {item.children.length === 0 ? (
          <span
            onClick={() => handleChildClick(makePath("services", item.title, null, "servicesMenu"))}
            style={{ cursor: "pointer" }}
          >
            {item.title}
          </span>
        ) : (
          <span>{item.title}</span>
        )}
        
        {item.children.length > 0 && <MdKeyboardArrowRight size={22} />}

        {hoverParentServices === index &&
          item.children.length > 0 && (
            <div className="sub-dropdown-box">
              {item.children.map((child, i) => (
                <div
                  key={i}
                  className="sub-dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChildClick(
                      makePath("services", item.title, child, "servicesMenu")
                    );
                  }}
                >
                  {child}
                </div>
              ))}
            </div>
          )}
      </div>
    ))}
  </div>
)}
            </li>

            {/* ---------------- INDUSTRIES ---------------- */}
            <li
              className="nav-item dropdown dropdown-parent"
              onMouseEnter={() =>
                !isMobileViewport() && setOpenIndustries(true)
              }
              onMouseLeave={() =>
                !isMobileViewport() &&
                (setOpenIndustries(false), setHoverParentIndustries(null))
              }
            >
              <div
                className="nav-link nav-btn dropdown-toggle-custom"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isMobileViewport()) setOpenIndustries((s) => !s);
                }}
              >
                <span>{t.industries}</span>
                <MdOutlineKeyboardArrowDown
                  size={20}
                  className="arrow-icon-nav"
                />
              </div>

              {openIndustries && <div className="dropdown-bridge"></div>}

              {(openIndustries || (!isMobileViewport() && openIndustries)) && (
                <div className="dropdown-box">
                  {industriesMenu.map((item, index) => (
                    <div
                      key={index}
                      className={`dropdown-item-row ${
                        hoverParentIndustries === index ? "active-parent" : ""
                      }`}
                      onMouseEnter={() =>
                        !isMobileViewport() && item.children.length > 0
                          ? setHoverParentIndustries(index)
                          : setHoverParentIndustries(null)
                      }
                      onClick={() => {
                        if (item.children.length === 0) {
                          handleChildClick(makePath("industries", item.title, null, "industriesMenu"));
                        } else if (isMobileViewport()) {
                          setHoverParentIndustries((prev) =>
                            prev === index ? null : index
                          );
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <span>{item.title}</span>
                      {item.children.length > 0 && (
                        <MdKeyboardArrowRight size={22} />
                      )}

                      {hoverParentIndustries === index &&
                        item.children.length > 0 && (
                          <div className="sub-dropdown-box">
                            {item.children.map((child, i) => (
                              <div
                                key={i}
                                className="sub-dropdown-item"
                                onClick={() =>
                                  handleChildClick(
                                    makePath("industries", item.title, child, "industriesMenu")
                                  )
                                }
                              >
                                {child}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              )}
            </li>

            <li className="nav-item">
              <a
                href="/blog"
                className={`nav-link nav-btn ${
                  currentPath === "/blog" ? "active-link" : ""
                }`}
              >
                {t.insight}
              </a>
            </li>

            <li className="nav-item">
              <a
                href="/careers"
                className={`nav-link nav-btn ${
                  currentPath === "/careers" ? "active-link" : ""
                }`}
              >
                {t.career}
              </a>
            </li>
          </ul>

          <div className="d-flex align-items-center right-buttons">
            <LanguageSelector
              changeLanguage={changeLanguage}
              currentLanguage={currentLanguage}
              renderTrigger={(lang) => (
                <div className="d-flex align-items-center lang-box">
                  <img
                    src={lang.flagImg}
                    alt={lang.name}
                    className="lang-flag"
                  />
                  <span className="lang-text">{lang.name}</span>
                  <MdOutlineKeyboardArrowDown size={20} color="white" />
                </div>
              )}
            />

            <div className="contact-box d-flex align-items-center">
              <a href="/contact" onClick={() => setMenuOpen(false)}>
                <span>{t.contactUs}</span>
              </a>
              <img
                src="/assets/icons/RightArrow.png"
                alt="arrow"
                className="arrow-icon"
              />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}